/**
 * Qué archivos acepta el sistema, en UN solo lugar.
 *
 * Vive acá porque la lista la necesitan tres capas —la pantalla que ofrece el
 * selector, el API que recibe el upload y el worker que lee el archivo— y
 * tenerlas por separado ya produjo una falla real: el `DropZone` ofrecía
 * imágenes, el API las aceptaba y el worker las descartaba en silencio, así que
 * el usuario subía la foto de un plano, veía el archivo cargado y nunca se
 * enteraba de que no se había leído ni una línea.
 *
 * Las IMÁGENES quedan afuera desde 2026-08-14, y por medición, no por falta de
 * lector: la misma lámina rasterizada de un PDF se lee exacta, y fotografiada con
 * poca luz o poca resolución se lee INVENTADA (diámetros que no figuran en la
 * hoja, y en el peor caso un plano completo que no existe). Como un diámetro
 * cambia el precio del caño, un dato inventado que se ve igual que uno leído es
 * peor que no tener el archivo. Cuando se pueda distinguir una foto legible de
 * una que no lo es, se agregan acá y se prende el camino de lectura por imagen,
 * que ya existe (`leerLaminasDesdeImagenes` en el worker).
 *
 * PLANOS CAD (.dwg/.dxf): se aceptan SOLO cuando el llamador pasa `dwgDxf: true`
 * (flag `DWG_DXF_ENABLED` en el API / `VITE_DWG_DXF_ENABLED` en la web). Con el
 * flag apagado se comportan como hasta ahora (rechazados al subir). El worker los
 * convierte a PDF con un sidecar (ACadSharp + ezdxf) y los lee por visión, igual
 * que un plano en PDF.
 */

/** Extensiones que se pueden subir siempre, con el punto y en minúsculas. */
export const EXTENSIONES_ACEPTADAS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.txt',
  '.csv',
  '.md',
] as const;

/**
 * Planos: CAD 2D (.dwg/.dxf, se convierten a PDF y se leen por visión) y modelos BIM 3D (.ifc, se
 * ven en el visor 3D del navegador). Todos aceptados solo con el flag `dwgDxf` prendido (el mismo que
 * enciende la feature de planos). El .ifc no lo lee el worker: lo abre el visor IFC en la pantalla, así
 * que el worker lo marca `skipped` en vez de descartarlo en silencio (evita "cargado pero ilegible").
 */
const DWG_DXF = ['.dwg', '.dxf', '.ifc'] as const;

/** Opciones que dependen de flags de despliegue. */
export interface OpcionesAceptacion {
  /** ¿Están habilitados los planos (CAD .dwg/.dxf + BIM .ifc)? Default false. */
  dwgDxf?: boolean;
}

/** Lista efectiva de extensiones aceptadas según los flags activos. */
export function extensionesAceptadas(opts: OpcionesAceptacion = {}): readonly string[] {
  return opts.dwgDxf ? [...EXTENSIONES_ACEPTADAS, ...DWG_DXF] : EXTENSIONES_ACEPTADAS;
}

/** Valor para el atributo `accept` de un `<input type="file">` según los flags. */
export function acceptAttr(opts: OpcionesAceptacion = {}): string {
  return extensionesAceptadas(opts).join(',');
}

/**
 * Valor `accept` con la lista BASE (sin planos CAD). Se mantiene por
 * compatibilidad; el código nuevo usa `acceptAttr({ dwgDxf })`.
 */
export const ACCEPT_ATTR = EXTENSIONES_ACEPTADAS.join(',');

/** Imágenes: se reconocen para poder explicar el motivo en vez de un error seco. */
const IMAGENES = /\.(jpg|jpeg|png|gif|bmp|webp|tif|tiff)$/i;
/** ZIP siempre rechazado (daba catálogos corruptos). */
const ZIP = /\.zip$/i;
/** Planos (CAD .dwg/.dxf + BIM .ifc): rechazados solo cuando el flag `dwgDxf` está apagado. */
const DWG_DXF_RE = /\.(dwg|dxf|ifc)$/i;

const extensionDe = (nombre: string): string => {
  const i = nombre.lastIndexOf('.');
  return i < 0 ? '' : nombre.slice(i).toLowerCase();
};

export const esImagen = (nombre: string): boolean => IMAGENES.test(nombre);

/** ¿Está rechazado explícitamente? (imagen, ZIP, o CAD con el flag apagado) */
export const esRechazado = (nombre: string, opts: OpcionesAceptacion = {}): boolean =>
  IMAGENES.test(nombre) || ZIP.test(nombre) || (!opts.dwgDxf && DWG_DXF_RE.test(nombre));

/** ¿Se puede subir? */
export const esAceptado = (nombre: string, opts: OpcionesAceptacion = {}): boolean =>
  extensionesAceptadas(opts).includes(extensionDe(nombre));

/**
 * Qué decirle al usuario cuando su archivo no entra.
 *
 * Se redacta acá y no en cada pantalla para que el mensaje del arrastre y el del
 * servidor sean el MISMO texto: si difieren, parece que uno de los dos se
 * equivocó. Dice qué hacer, no solo qué falló.
 */
export function motivoDeRechazo(nombres: string[], opts: OpcionesAceptacion = {}): string {
  const lista = nombres.join(', ');
  if (nombres.some(esImagen)) {
    return `Todavía no podemos leer imágenes (${lista}). Si es un plano o algo escaneado, guárdalo en PDF y súbelo así: de esa forma lo leemos completo.`;
  }
  if (!opts.dwgDxf && nombres.some((n) => DWG_DXF_RE.test(n))) {
    return `Todavía no podemos leer planos de AutoCAD ni modelos BIM (${lista}). Si lo tienes en PDF, súbelo así y lo leemos.`;
  }
  return `Este tipo de archivo no es compatible (${lista}). Sube PDF, Word, Excel o texto.`;
}
